import {
    createEventTitle,
    IEventProperties
} from '~/services/analytics/analytics.service';
import { IAnalyticsService } from '~/services/interfaces/IAnalyticsService';
import { isProduction } from '~/apps/Router/router.utils';
import { getMobileDevice, isInAppBrowser } from '~/utils/device.utils';
import { extractDomain } from '~/utils/url.utils';
import { ConfigStore } from '~/stores/config.store';
import { getScreenResolution } from '~/services/screen.service';
import { FullstoryService } from '~/services/fullstory/fullstory.service';
import { signupDataFromMarketing } from '~/components/Signup/utils/signup-form.utils';
import { isReactFirst } from '~/utils/environment.utils';

type UnauthorizedAnalyticsServiceProps = {
    configStore: ConfigStore;
    fullstoryService: FullstoryService;
};

export class UnauthenticatedAnalyticsService implements IAnalyticsService {
    configStore: ConfigStore;
    fullstoryService: FullstoryService;
    initialReferrer: string;
    hbAnonymousId: string;

    constructor({
        configStore,
        fullstoryService
    }: UnauthorizedAnalyticsServiceProps) {
        this.configStore = configStore;
        this.fullstoryService = fullstoryService;
        this.initialReferrer = document.referrer;
    }

    private isSegmentLoaded() {
        return !!window.analytics;
    }

    track(
        eventName: string,
        properties: IEventProperties
    ): void | Promise<any> {
        const eventTitle = createEventTitle(eventName, properties);

        const defaultProperties = {
            hb_version: this.configStore.config.version,
            hb_api_version: this.configStore.config.api_version,
            screen_resolution: getScreenResolution(this.fullstoryService),
            is_react_first: isReactFirst(),
            is_in_app_browser: isInAppBrowser(),
            platform: 'react',
            hb_anonymous_id: this.hbAnonymousId
        };
        const marketingSignupDataProperties =
            location.pathname === '/signup' ? signupDataFromMarketing : {};

        if (!isProduction() || window.enableAnalyticsConsoleLogging) {
            // eslint-disable-next-line no-console
            console.log(
                `[tracking event] ${eventTitle} |  ${JSON.stringify(
                    {
                        ...defaultProperties,
                        ...properties,
                        ...marketingSignupDataProperties
                    },
                    null,
                    2
                )}`
            );
            return;
        } // TODO create logger service to enable track debug in non-production environments

        this.fullstoryService.trackFullstoryEvent({
            event: eventName,
            props: properties
        });

        if (this.isSegmentLoaded()) {
            const deviceProperties = {
                $screen_width: window.screen.width,
                $screen_height: window.screen.height,
                $device: getMobileDevice(),
                $initial_referrer: this.initialReferrer,
                $initial_referring_domain: extractDomain(this.initialReferrer),
                $referring_domain: extractDomain(document.referrer)
            };
            // @ts-ignore
            return window.analytics.track(eventTitle, {
                ...defaultProperties,
                ...deviceProperties,
                ...properties,
                ...marketingSignupDataProperties
            });
        }
    }

    overrideSegmentAnonymousId(id: string, traits = {}) {
        if (!this.isSegmentLoaded()) {
            return;
        }

        window.analytics?.identify(undefined, traits, {
            anonymousId: id
        });
    }

    setHBAnonymousId(id: string) {
        this.hbAnonymousId = id;
    }
}
